/**
 * Global amount formatting utility
 * Format: 1.000,50 (period for thousands, comma for decimal)
 * Hides .00 when there are no decimal places: 1.000 MAD instead of 1.000,00 MAD
 */

/**
 * Formats a number according to the global amount formatting rules
 * @param {number|string} amount - The amount to format
 * @param {boolean} showCurrency - Whether to append " MAD" (default: true)
 * @param {boolean} showSign - Whether to show + or - sign (default: false)
 * @returns {string} Formatted amount string
 */
export const formatAmount = (amount, showCurrency = true, showSign = false) => {
  if (amount === null || amount === undefined || amount === '') {
    return showCurrency ? '0 MAD' : '0';
  }

  // Convert to number if it's a string
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) {
    return showCurrency ? '0 MAD' : '0';
  }

  // Check if the number has decimal places
  const hasDecimals = numAmount % 1 !== 0;
  
  // Format with conditional decimal places and custom thousands separator
  const formatted = numAmount.toLocaleString('fr-FR', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
    useGrouping: true
  }).replace(/[\s\u00A0]/g, '.'); // Replace both regular spaces and non-breaking spaces with periods

  // Add sign if requested
  let result = formatted;
  if (showSign && numAmount !== 0) {
    result = (numAmount > 0 ? '+' : '') + formatted;
  }

  // Add currency if requested
  if (showCurrency) {
    result += ' MAD';
  }

  return result;
};

/**
 * Formats an amount for display in tables (with color coding)
 * @param {number|string} amount - The amount to format
 * @param {boolean} showSign - Whether to show + or - sign (default: true)
 * @returns {object} Object with formatted text and color
 */
export const formatAmountForDisplay = (amount, showSign = true) => {
  const formatted = formatAmount(amount, true, showSign);
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  let color = '#38485C'; // Default color
  if (showSign && !isNaN(numAmount)) {
    if (numAmount > 0) {
      color = '#0AD69F'; // Green for positive
    } else if (numAmount < 0) {
      color = '#EF456E'; // Red for negative
    }
  }

  return {
    text: formatted,
    color: color
  };
};

/**
 * Formats an amount for input fields (removes currency, keeps formatting)
 * @param {number|string} amount - The amount to format
 * @returns {string} Formatted amount without currency
 */
export const formatAmountForInput = (amount) => {
  return formatAmount(amount, false, false);
};

/**
 * Parses a formatted amount string back to a number
 * @param {string} formattedAmount - The formatted amount string
 * @returns {number} Parsed number
 */
export const parseFormattedAmount = (formattedAmount) => {
  if (!formattedAmount) return 0;
  
  // Remove currency and spaces
  let cleaned = formattedAmount.replace(/[^\d,.-]/g, '');
  
  // Replace comma with dot for decimal parsing
  cleaned = cleaned.replace(',', '.');
  
  // Remove thousand separators (periods that are not followed by exactly 2 digits)
  cleaned = cleaned.replace(/\.(?=\d{3})/g, '');
  
  return parseFloat(cleaned) || 0;
};

const amountFormatter = {
  formatAmount,
  formatAmountForDisplay,
  formatAmountForInput,
  parseFormattedAmount
};

export default amountFormatter;
